import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { supabase } from '@/lib/supabase';
import CampoTexto from '@/components/base/CampoTexto';
import { EMAIL_REGEX } from '@/pages/inscricao/utils';
import { formatarData, formatarPeriodo, formatarValor } from '@/pages/evento/utils';
import type { EventoDraft, FormaParticipar, MembroEquipe, ModoPagamento, PassoChave } from '../types';

export interface PassoPublicarHandle {
  validar: () => boolean;
}

interface PassoPublicarProps {
  draft: EventoDraft;
  participacoes: FormaParticipar[];
  equipe: MembroEquipe[];
  dono: boolean;
  eventoId: number | null;
  onChange: (patch: Partial<EventoDraft>) => void;
  onPendenciaChange: (faltam: number) => void;
  aoEditar: (passo: PassoChave) => void;
  aoConvidar: (email: string) => Promise<{ ok: boolean; mensagem: string }>;
  aoRemoverMembro: (email: string) => void;
}

function rotuloForma(modo: ModoPagamento): string {
  switch (modo) {
    case 'plataforma':
      return 'Pix na plataforma';
    case 'rifa':
      return 'sorteio';
    case 'gratuito':
      return 'gratuito';
    case 'direto':
      return 'direto com a organização';
  }
}

function PassoPublicar(
  { draft, participacoes, equipe, dono, eventoId, onChange, onPendenciaChange, aoEditar, aoConvidar, aoRemoverMembro }: PassoPublicarProps,
  ref: React.Ref<PassoPublicarHandle>,
) {
  const [temRecebedor, setTemRecebedor] = useState<boolean>(true);
  const [erroRecebedor, setErroRecebedor] = useState<string | null>(null);
  const [recebedorInfo, setRecebedorInfo] = useState<{
    nome: string;
    chave_mascarada: string;
    documento_mascarado: string | null;
  }>({ nome: '', chave_mascarada: '', documento_mascarado: null });
  const [fichaResumo, setFichaResumo] = useState<Map<number, number>>(new Map());
  const [emailConvite, setEmailConvite] = useState('');
  const [erroEmail, setErroEmail] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erroConvite, setErroConvite] = useState<string | null>(null);
  const [convidando, setConvidando] = useState(false);
  const [datas, setDatas] = useState<{ id: number; nome: string; inicio_em: string }[]>([]);

  const bloqueiaPorRecebedor = draft.modoPagamento === 'plataforma' && !temRecebedor;

  useEffect(() => {
    let ativo = true;
    const ids = participacoes
      .filter((p) => p.temFormulario && p.formularioId !== null)
      .map((p) => p.formularioId as number);
    if (ids.length === 0) {
      setFichaResumo(new Map());
      return () => {
        ativo = false;
      };
    }
    supabase
      .from('perguntas')
      .select('formulario_id')
      .in('formulario_id', ids)
      .then(({ data }) => {
        if (!ativo) return;
        const map = new Map<number, number>();
        ((data as { formulario_id: number }[]) || []).forEach((r) => {
          map.set(r.formulario_id, (map.get(r.formulario_id) ?? 0) + 1);
        });
        setFichaResumo(map);
      });
    return () => {
      ativo = false;
    };
  }, [participacoes]);

  useEffect(() => {
    if (!eventoId) return;
    let ativo = true;
    supabase
      .from('vw_encontros_evento')
      .select('id, nome, inicio_em')
      .eq('evento_id', eventoId)
      .order('inicio_em')
      .then(({ data }) => {
        if (ativo) setDatas((data as { id: number; nome: string; inicio_em: string }[]) || []);
      });
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  // Quem recebe o repasse (para a regra de publicar e o resumo do dinheiro).
  useEffect(() => {
    if (!eventoId) {
      setTemRecebedor(false);
      return;
    }
    let ativo = true;
    supabase
      .rpc('recebedor_do_evento', { p_evento: eventoId })
      .then(({ data, error }) => {
        if (!ativo) return;
        if (error) {
          setErroRecebedor('Não consegui conferir para onde vai o dinheiro. Tente de novo.');
          return;
        }
        setErroRecebedor(null);
        const d = data as {
          tem?: boolean;
          nome?: string;
          chave_mascarada?: string;
          documento_mascarado?: string | null;
        } | null;
        setTemRecebedor(d?.tem === true);
        setRecebedorInfo({
          nome: d?.nome || '',
          chave_mascarada: d?.chave_mascarada || '',
          documento_mascarado: d?.documento_mascarado || null,
        });
      });
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  useEffect(() => {
    onPendenciaChange(bloqueiaPorRecebedor ? 1 : 0);
  }, [bloqueiaPorRecebedor, onPendenciaChange]);

  useImperativeHandle(
    ref,
    () => ({
      validar() {
        if (bloqueiaPorRecebedor) {
          document
            .getElementById('pendencia-dinheiro')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
        return true;
      },
    }),
    [bloqueiaPorRecebedor],
  );

  async function convidar() {
    const email = emailConvite.trim();
    if (!email) {
      setErroEmail('Digite o e-mail da pessoa.');
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setErroEmail('Digite um e-mail válido, como maria@gmail.com.');
      return;
    }
    setErroEmail(null);
    setAviso(null);
    setErroConvite(null);
    setConvidando(true);
    const res = await aoConvidar(email);
    setConvidando(false);
    if (res.ok) {
      setEmailConvite('');
      setAviso(res.mensagem);
    } else if (res.mensagem) {
      setErroConvite(res.mensagem);
    }
  }

  function corStatus(status: string): string {
    if (status === 'ativo') return 'text-green-600';
    if (status === 'recusado') return 'text-amber-600';
    return 'text-secondary-500';
  }

  const dataEvento = draft.inicioData
    ? formatarPeriodo(`${draft.inicioData}T00:00`, `${draft.fimData}T23:59`)
    : '';

  const pagos = participacoes.filter((p) => p.valorModo === 'pago');
  const repasse =
    recebedorInfo.documento_mascarado && recebedorInfo.documento_mascarado !== recebedorInfo.chave_mascarada
      ? recebedorInfo.documento_mascarado
      : recebedorInfo.chave_mascarada;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-normal text-foreground-950">Revisar e publicar</h2>
        <p className="mt-1 text-sm text-secondary-500">Confira tudo e coloque no ar.</p>
      </div>

      {erroRecebedor && (
        <section className="flex items-start gap-2.5 rounded-lg border border-danger-200 bg-danger-50 px-4 py-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-danger-600">
            <i className="ri-alert-line" />
          </span>
          <p className="text-sm text-danger-700">{erroRecebedor}</p>
        </section>
      )}

      {bloqueiaPorRecebedor && (
        <section
          id="pendencia-dinheiro"
          className="overflow-hidden rounded-lg border border-secondary-200 bg-white"
        >
          <h4 className="border-b border-secondary-100 px-5 py-3 text-sm font-medium text-foreground-950">
            Para publicar, falta
          </h4>
          <button
            type="button"
            onClick={() => aoEditar('pagamento')}
            className="flex w-full cursor-pointer items-center gap-3 px-5 py-2.5 text-left transition-colors hover:bg-background-50"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center text-amber-500">
              <i className="ri-alert-line" />
            </span>
            <span className="text-sm text-foreground-900">Para onde vai o dinheiro</span>
            <span className="ml-auto flex items-center gap-1 whitespace-nowrap text-sm text-primary-600">
              indicar
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </span>
          </button>
        </section>
      )}

      {/* Resumo: Evento */}
      <div className="rounded-lg border border-secondary-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-foreground-950">Evento</h3>
          <button
            type="button"
            onClick={() => aoEditar('evento')}
            className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            editar
          </button>
        </div>
        <div className="mt-2 space-y-1 text-[15px] text-foreground-800">
          <p className="font-medium text-foreground-950">{draft.nome || 'Sem nome'}</p>
          {dataEvento && <p>{dataEvento}</p>}
          {(draft.localNome || draft.cidade) && (
            <p>
              {[draft.localNome, draft.cidade].filter(Boolean).join(' · ')}
              {draft.uf ? `, ${draft.uf}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Resumo: Tipos de inscrição */}
      <div className="rounded-lg border border-secondary-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-foreground-950">Tipos de inscrição</h3>
          <button
            type="button"
            onClick={() => aoEditar('inscricoes')}
            className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            editar
          </button>
        </div>
        <div className="mt-3 divide-y divide-secondary-100">
          {participacoes.map((p, i) => {
            const aDefinir = p.valorModo === 'a_definir';
            const valor = aDefinir
              ? 'Valor a definir'
              : p.dividirGrupos
                ? p.grupos.map((g) => formatarValor(g.valorCentavos)).join(' / ')
                : formatarValor(p.valorCentavos);
            const vagas = p.temLimite ? `${p.vagas} vagas` : 'sem limite';
            const abre = p.inscricoesAbremData
              ? `abre ${formatarData(`${p.inscricoesAbremData}T00:00`)}`
              : 'abre ao publicar';
            if (aDefinir) {
              return (
                <div key={i} className="py-3 first:pt-0 last:pb-0">
                  <span className="inline-flex flex-wrap items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-sm text-amber-800">
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-alert-line" />
                    </span>
                    <span className="font-medium">
                      {p.nome || `tipo de inscrição ${i + 1}`}
                    </span>
                    <span>· Valor a definir — ninguém consegue se inscrever ainda</span>
                  </span>
                </div>
              );
            }
            return (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0">
                <span className="font-medium text-foreground-950">
                  {p.nome || `tipo de inscrição ${i + 1}`}
                </span>
                <span className="text-sm text-secondary-500">
                  {valor} · {vagas} · {abre}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resumo: Como se paga */}
      <div className="rounded-lg border border-secondary-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-foreground-950">Como se paga</h3>
          <button
            type="button"
            onClick={() => aoEditar('pagamento')}
            className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            editar
          </button>
        </div>
        <div className="mt-3 space-y-1.5 text-[15px] text-foreground-800">
          {draft.modoPagamento === 'gratuito' ? (
            <p>Não se cobra nada. A pessoa só preenche a ficha.</p>
          ) : (
            <>
              {pagos.length === 0 ? (
                <p className="text-secondary-500">Nenhum tipo de inscrição pago.</p>
              ) : (
                pagos.map((p, i) => {
                  const valor = p.dividirGrupos
                    ? p.grupos.map((g) => formatarValor(g.valorCentavos)).join(' / ')
                    : formatarValor(p.valorCentavos);
                  return (
                    <p key={i}>
                      <span className="font-medium text-foreground-950">
                        {p.nome || `tipo de inscrição ${i + 1}`}
                      </span>{' '}
                      {valor} · pago por {rotuloForma(draft.modoPagamento)}
                    </p>
                  );
                })
              )}
              {draft.modoPagamento === 'plataforma' && recebedorInfo.nome && (
                <p className="text-secondary-600">
                  Repasse para: {recebedorInfo.nome}
                  {repasse ? ` (${repasse})` : ''}
                </p>
              )}
              {draft.modoPagamento === 'rifa' && (
                <p className="text-secondary-600">A inscrição é paga pela ação entre amigos deste evento.</p>
              )}
              {draft.modoPagamento === 'direto' && (
                <p className="text-secondary-600">
                  {draft.textoPagamento.trim() || 'Pagamento combinado direto com a organização.'}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Resumo: Datas do evento */}
      <div className="rounded-lg border border-secondary-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-foreground-950">Datas do evento</h3>
          <button
            type="button"
            onClick={() => aoEditar('datas')}
            className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            editar
          </button>
        </div>
        <div className="mt-3 divide-y divide-secondary-100">
          {datas.length === 0 ? (
            <p className="py-2 text-sm text-secondary-500">Nenhuma data cadastrada.</p>
          ) : (
            datas.map((d) => (
              <div
                key={d.id}
                className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0"
              >
                <span className="font-medium text-foreground-950">{d.nome}</span>
                <span className="text-sm text-secondary-500">
                  {new Date(d.inicio_em).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Resumo: Ficha */}
      {draft.formaParticipar !== 'ingresso' && (
        <div className="rounded-lg border border-secondary-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium text-foreground-950">Ficha</h3>
            <button
              type="button"
              onClick={() => aoEditar('ficha')}
              className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              editar
            </button>
          </div>
          <div className="mt-3 divide-y divide-secondary-100">
            {participacoes.map((p, i) => {
              const total = p.formularioId ? fichaResumo.get(p.formularioId) ?? 0 : 0;
              return (
                <div key={i} className="flex items-center justify-between gap-2 py-3 first:pt-0 last:pb-0">
                  <span className="font-medium text-foreground-950">
                    {p.nome || `tipo de inscrição ${i + 1}`}
                  </span>
                  <span className="text-sm text-secondary-500">
                    {p.temFormulario
                      ? total === 1
                        ? '1 pergunta'
                        : `${total} perguntas`
                      : 'sem ficha'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Contato de dúvidas */}
      <div className="rounded-lg border border-secondary-200 bg-white p-5">
        <h3 className="text-base font-medium text-foreground-950">Com quem falar sobre dúvidas</h3>
        <p className="mt-1 text-sm text-secondary-500">
          Aparece no rodapé da página do evento, com link para o WhatsApp.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CampoTexto
            id="contato-nome"
            name="contato_nome"
            label="Nome"
            value={draft.contatoNome}
            onChange={(v) => onChange({ contatoNome: v })}
            placeholder="Maria da Silva"
          />
          <CampoTexto
            id="contato-telefone"
            name="contato_telefone"
            label="Telefone"
            value={draft.contatoTelefone}
            onChange={(v) => onChange({ contatoTelefone: v })}
            placeholder="(14) 99999-9999"
            inputMode="tel"
          />
        </div>
      </div>

      {/* Equipe */}
      {dono && (
        <div className="rounded-lg border border-secondary-200 bg-white p-5">
          <h3 className="text-base font-medium text-foreground-950">
            Quem mais administra este evento?
          </h3>
          <p className="mt-1 text-sm text-secondary-500">
            A pessoa vê o convite ao entrar na conta dela, com este mesmo e-mail. Avise ela pelo
            WhatsApp.
          </p>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <div className="flex-1">
              <CampoTexto
                id="email-convite"
                name="email_convidado"
                label="E-mail"
                value={emailConvite}
                onChange={(v) => {
                  setEmailConvite(v);
                  if (erroEmail) setErroEmail(null);
                }}
                error={erroEmail}
                placeholder="maria@gmail.com"
                inputMode="email"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={convidar}
                disabled={convidando}
                className="w-full cursor-pointer whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {convidando ? 'Convidando…' : 'Convidar'}
              </button>
            </div>
          </div>

          {aviso && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2.5">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-green-600">
                <i className="ri-check-line" />
              </span>
              <p className="text-sm text-green-800">{aviso}</p>
            </div>
          )}

          {erroConvite && (
            <p className="mt-3 rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">
              {erroConvite}
            </p>
          )}

          {equipe.length > 0 && (
            <ul className="mt-3 divide-y divide-secondary-100">
              {equipe.map((m) => (
                <li key={m.id} className="flex items-center justify-between py-2">
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] text-foreground-800">
                      {m.email}
                    </span>
                    <span className={`block text-xs ${corStatus(m.status)}`}>
                      {m.oQueSignifica}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => aoRemoverMembro(m.email)}
                    aria-label="Remover membro"
                    className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 hover:text-danger-600"
                  >
                    <i className="ri-close-line text-lg" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default forwardRef(PassoPublicar);